import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, X, Loader2, ChevronLeft, ChevronRight, GripVertical } from "lucide-react";
import { useBienPhotoUpload } from "@/hooks/useBienPhotoUpload";
import type { BienPhoto } from "@/types/bien";

interface PhotoGalleryProps {
  bienId: string;
  value: BienPhoto[];
  onChange: (next: BienPhoto[]) => void;
  readOnly?: boolean;
}

export function PhotoGallery({ bienId, value, onChange, readOnly }: PhotoGalleryProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadMany, removeOne, uploading } = useBienPhotoUpload(bienId);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const photos = value ?? [];

  async function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ""; // reset to allow re-selecting same file
    if (files.length === 0) return;
    setError(null);
    try {
      const uploaded = await uploadMany(files);
      onChange([...photos, ...uploaded]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    }
  }

  async function handleDelete(idx: number) {
    if (!confirm(t("biens.photosDeleteConfirm"))) return;
    const photo = photos[idx];
    setError(null);
    try {
      await removeOne(photo.path);
      onChange(photos.filter((_, i) => i !== idx));
      if (lightboxIdx !== null && lightboxIdx >= photos.length - 1) {
        setLightboxIdx(photos.length > 1 ? photos.length - 2 : null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    }
  }

  function handleDragStart(idx: number) {
    setDragIdx(idx);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
  }

  function handleDrop(targetIdx: number) {
    if (dragIdx === null || dragIdx === targetIdx) {
      setDragIdx(null);
      return;
    }
    const next = [...photos];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, moved);
    setDragIdx(null);
    onChange(next);
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-text-muted">
          {photos.length > 0 && !readOnly ? t("biens.photosDragHint") : ""}
        </p>
        {!readOnly && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading > 0}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-accent text-white hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-50"
          >
            {uploading > 0 ? (
              <>
                <Loader2 size={13} className="animate-spin" />
                {t("biens.photosUploading", { count: uploading })}
              </>
            ) : (
              <>
                <Plus size={13} />
                {t("biens.photosAdd")}
              </>
            )}
          </button>
        )}
      </div>

      {/* Hidden file input */}
      {!readOnly && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          multiple
          onChange={handleFiles}
          className="hidden"
        />
      )}

      {/* Error banner */}
      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5">
          {error}
        </div>
      )}

      {/* Empty state */}
      {photos.length === 0 && (
        <div className="text-sm text-text-muted py-4 text-center border border-dashed border-border rounded">
          {t("biens.photosEmpty")}
        </div>
      )}

      {/* Grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {photos.map((p, idx) => (
            <div
              key={p.id}
              draggable={!readOnly}
              onDragStart={() => handleDragStart(idx)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(idx)}
              className={`relative group rounded overflow-hidden border border-border bg-surface aspect-square ${
                !readOnly ? "cursor-move" : "cursor-pointer"
              } ${dragIdx === idx ? "opacity-50" : ""}`}
              onClick={(e) => {
                // ne pas ouvrir lightbox si l'utilisateur a cliqué sur un bouton
                if ((e.target as HTMLElement).closest("button")) return;
                setLightboxIdx(idx);
              }}
            >
              <img
                src={p.url}
                alt=""
                className="w-full h-full object-cover"
                draggable={false}
              />

              {/* Cover badge */}
              {idx === 0 && (
                <span className="absolute top-1 left-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-accent text-white rounded font-semibold">
                  {t("biens.photosCover")}
                </span>
              )}

              {/* Drag handle hint (visible on hover) */}
              {!readOnly && (
                <div className="absolute top-1 right-8 opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 text-white p-1 rounded">
                  <GripVertical size={12} />
                </div>
              )}

              {/* Delete button */}
              {!readOnly && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(idx);
                  }}
                  className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-red-600 text-white p-1 rounded cursor-pointer"
                  aria-label="Delete"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIdx !== null && photos[lightboxIdx] && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxIdx(null)}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIdx(null);
            }}
            className="absolute top-4 right-4 text-white p-2 rounded-full hover:bg-white/10 cursor-pointer"
            aria-label="Close"
          >
            <X size={24} />
          </button>
          {lightboxIdx > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIdx(lightboxIdx - 1);
              }}
              className="absolute left-4 text-white p-2 rounded-full hover:bg-white/10 cursor-pointer"
              aria-label="Previous"
            >
              <ChevronLeft size={32} />
            </button>
          )}
          {lightboxIdx < photos.length - 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIdx(lightboxIdx + 1);
              }}
              className="absolute right-4 text-white p-2 rounded-full hover:bg-white/10 cursor-pointer"
              aria-label="Next"
            >
              <ChevronRight size={32} />
            </button>
          )}
          <img
            src={photos[lightboxIdx].url}
            alt=""
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <span className="absolute bottom-4 text-white text-xs">
            {lightboxIdx + 1} / {photos.length}
          </span>
        </div>
      )}
    </div>
  );
}
